import React from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  MANAGER_APPLICATION_REVIEW_DOCUMENTS_ROWS_PER_PAGE_OPTIONS,
  MANAGER_APPLICATION_REVIEW_EMPTY_TEXT,
  MANAGER_APPLICATION_REVIEW_TABLE_LABELS,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import useManagerApplicationDocumentsTableForm from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useManagerApplicationDocumentsTableForm.ts';
import {
  buildManagerReviewTableRows,
  normalizeManagerDocumentFiles,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/reviewUtils.ts';
import { DocumentRow } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReviewDocuments/DocumentRow.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import type { TApiSupervisorApplicationDocument } from '~/modules/covenants/types.ts';
import type {
  TManagerApplicationDecision,
  TManagerReviewFile,
  TManagerReviewTableRow,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import type { TManagerViewedDocumentsById } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/managerViewedDocumentsStorage.ts';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';

interface IManagerApplicationDocumentsTableProps {
  documents: TApiSupervisorApplicationDocument[];
  decisions: Record<string, TManagerApplicationDecision>;
  isReadOnly: boolean;
  page: number;
  rowsPerPage: number;
  totalElements: number;
  isLoading: boolean;
  comments: Record<string, string>;
  updatingDecisionDocumentIds: Record<string, boolean>;
  viewedDocumentIds: TManagerViewedDocumentsById;
  getDocumentCommentValue: (documentId: string) => string;
  onPreviewDocument: (document: TManagerReviewFile) => void;
  onDecisionChange: (
    documentId: string,
    decision: Exclude<TManagerApplicationDecision, null>
  ) => void;
  onCommentSave: (documentId: string, comment: string) => Promise<void> | void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export const DocumentsTable: React.FC<IManagerApplicationDocumentsTableProps> = ({
  documents,
  decisions,
  isReadOnly,
  page,
  rowsPerPage,
  totalElements,
  isLoading,
  comments,
  updatingDecisionDocumentIds,
  viewedDocumentIds,
  getDocumentCommentValue,
  onPreviewDocument,
  onDecisionChange,
  onCommentSave,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const reviewFiles = React.useMemo(
    () => documents.flatMap(normalizeManagerDocumentFiles),
    [documents]
  );
  const tableRows = React.useMemo(() => buildManagerReviewTableRows(documents), [documents]);
  const formMethods = useManagerApplicationDocumentsTableForm({
    reviewFiles,
    comments,
  });
  const isRowViewed = React.useCallback(
    (row: TManagerReviewTableRow) =>
      row.type === 'file' && (viewedDocumentIds[row.file.id] ?? false),
    [viewedDocumentIds]
  );

  return (
    <FormProvider {...formMethods}>
      <Box sx={managerComponentStyles.docsRoot}>
        <Box sx={commonStyles.loadingWrap}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={managerComponentStyles.docsTableContainer}
          >
            <Table
              stickyHeader
              sx={managerComponentStyles.docsTableRoot}
              aria-label="manager application documents table"
            >
              <TableHead sx={managerComponentStyles.docsTableHead}>
                <TableRow sx={managerComponentStyles.docsTableHeaderRow}>
                  <TableCell align="left" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.documentName}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.term}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.status}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.comment}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.signer}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.period}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.preview}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.accept}
                  </TableCell>
                  <TableCell align="center" sx={managerComponentStyles.docsHeaderCell}>
                    {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.reject}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => (
                  <DocumentRow
                    key={row.key}
                    row={row}
                    decisions={decisions}
                    isDecisionUpdating={
                      row.type === 'file' && Boolean(updatingDecisionDocumentIds[row.file.id])
                    }
                    isReadOnly={isReadOnly}
                    isViewed={isRowViewed(row)}
                    getDocumentCommentValue={getDocumentCommentValue}
                    onPreviewDocument={onPreviewDocument}
                    onDecisionChange={onDecisionChange}
                    onCommentSave={onCommentSave}
                  />
                ))}

                {!tableRows.length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={dataFormStyles.emptyCell}>
                      {MANAGER_APPLICATION_REVIEW_EMPTY_TEXT}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {isLoading && (
            <Box sx={commonStyles.loadingMask}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>

        {totalElements > 0 && (
          <Box sx={managerStyles.tablePagination}>
            <TablePaginationControls
              page={page}
              rowsPerPage={rowsPerPage}
              totalElements={totalElements}
              isLoading={isLoading}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={MANAGER_APPLICATION_REVIEW_DOCUMENTS_ROWS_PER_PAGE_OPTIONS}
            />
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};

export default DocumentsTable;

